import React, { useState, useEffect, useRef } from 'react';
import { Pause, PlayCircle } from "@mui/icons-material";
import Draggable from "react-draggable";
export const ReviewTimer = ({ patientId, draggable, overrideValue }) => {
    const [currentTime, setCurrentTime] = useState(0);
    const [isRunning, setIsRunning] = useState(true);
    const intervalRef = useRef(null);
    const storageKey = `patient-timer-${patientId}`;
    useEffect(() => {
        if (overrideValue) {
            setIsRunning(false);
        }
    }, [overrideValue]);
    // Timer data stored in state (in real app, this would come from sessionStorage)
    const [timerData, setTimerData] = useState({
        startTime: Date.now(),
        totalPausedTime: 0,
        isRunning: true,
        lastPauseTime: null
    });
    // Format time to HH:MM:SS format
    const formatTime = (totalMs) => {
        const totalSeconds = Math.floor(totalMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };
    // Calculate elapsed time from timer data
    const calculateElapsedTime = () => {
        const now = Date.now();
        let elapsed = now - timerData.startTime - timerData.totalPausedTime;
        // If currently paused, don't count time since last pause
        if (!timerData.isRunning && timerData.lastPauseTime) {
            elapsed -= (now - timerData.lastPauseTime);
        }
        return Math.max(0, elapsed);
    };
    // Update timer display
    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        if (isRunning) {
            intervalRef.current = setInterval(() => {
                setCurrentTime(calculateElapsedTime());
            }, 1000);
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isRunning, timerData]);
    // Initialize timer from sessionStorage on mount
    useEffect(() => {
        const storedData = sessionStorage.getItem(storageKey);
        if (storedData) {
            const parsed = JSON.parse(storedData);
            setTimerData(parsed);
            setIsRunning(parsed.isRunning);
        }
        setCurrentTime(calculateElapsedTime());
    }, [patientId]);
    const handlePause = () => {
        setIsRunning(false);
        const updatedData = {
            ...timerData,
            isRunning: false,
            lastPauseTime: Date.now()
        };
        setTimerData(updatedData);
        sessionStorage.setItem(storageKey, JSON.stringify(updatedData));
    };
    const handleResume = () => {
        setIsRunning(true);
        const pauseDuration = timerData.lastPauseTime ? Date.now() - timerData.lastPauseTime : 0;
        const updatedData = {
            ...timerData,
            totalPausedTime: timerData.totalPausedTime + pauseDuration,
            isRunning: true,
            lastPauseTime: null
        };
        setTimerData(updatedData);
        sessionStorage.setItem(storageKey, JSON.stringify(updatedData));
    };
    if (draggable) {
        return (React.createElement(Draggable, { handle: "#draggable-icon", cancel: '[class*="MuiDialogContent-root"]' },
            React.createElement("div", { style: { position: 'relative', width: 'fit-content', zIndex: 1000 } },
                React.createElement("div", { id: "draggable-icon", style: { cursor: 'move', padding: '3px', backgroundColor: '#f0f0f0' } }, "\u22EE\u22EE "),
                React.createElement("div", { className: "flex items-center gap-3 p-3 bg-gray-50 rounded-lg" },
                    React.createElement("div", { className: "text-2xl font-mono font-bold text-gray-800" }, formatTime(currentTime)),
                    React.createElement("button", { onClick: isRunning ? handlePause : handleResume, className: `rounded-full ${isRunning
                            ? 'bg-blue-500 hover:bg-blue-600'
                            : 'bg-gray-400 hover:bg-gray-500'} text-white transition-colors`, style: { height: '24px', width: '24px' } }, isRunning ? React.createElement(Pause, { style: { height: '14px', width: '14px', marginLeft: 'auto', marginRight: 'auto', verticalAlign: 'inherit' } }) : React.createElement(PlayCircle, { style: { height: '14px', width: '14px', marginLeft: 'auto', marginRight: 'auto', verticalAlign: 'inherit' } }))))));
    }
    else {
        return (React.createElement("div", { className: "flex items-center gap-3 pt-4 pb-4 rounded-lg" },
            React.createElement("div", { style: { textAlign: 'center', width: '100%', padding: '15px', border: '1px solid #E0E0E0', borderRadius: '10px' } },
                React.createElement("div", { style: { fontFamily: 'Barlow', fontWeight: '400', color: '#000' } }, overrideValue ? formatTime(overrideValue * 60000) : formatTime(currentTime)))));
    }
};
